import { useRef, useEffect } from "react";
import { Container, Typography, Stack, Box, Divider } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import { useNavigate, useLocation } from "react-router";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

// --- Sub-components for Delivery Note ---

const PrintHeader = ({ data, company, companyLogo }) => {
  return (
    <Stack direction="row" sx={{ height: "11rem", borderBottom: "1px solid black" }}>
      {/* Left Box: Logo & Company Address */}
      <div
        style={{
          width: "50%",
          borderRight: "1px solid black",
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {companyLogo && (
          <div style={{ height: "4rem", width: "15rem", marginBottom: "0.5rem" }}>
            <img
              src={companyLogo}
              style={{
                objectFit: "contain",
                height: "100%",
                width: "100%",
              }}
              alt="Company Logo"
            />
          </div>
        )}

        <Container disableGutters sx={{ color: "black" }}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: "bold", mb: 0.2, lineHeight: 1.2 }}>
            {company.Name}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", lineHeight: "1.3" }}>
            {company.Add1}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", lineHeight: "1.3" }}>
            {company.Place}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", lineHeight: "1.3" }}>
            Ph: {company.Ph} | VAT: {company.Add2}
          </Typography>
          {company.Web && (
            <Typography sx={{ fontSize: "0.8rem", lineHeight: "1.3" }}>
              {company.Web}
            </Typography>
          )}
        </Container>
      </div>

      {/* Right Box: Delivery Note Metadata & Destination */}
      <Stack direction="column" sx={{ width: "50%", padding: "1rem", justifyContent: "space-between" }}>
        <div>
          <Typography
            align="right"
            variant="h3"
            sx={{
              fontSize: "1.8rem",
              fontWeight: "bold",
              color: "#1b5e20",
              letterSpacing: "1px",
              mb: 1
            }}
          >
            DELIVERY NOTE
          </Typography>
          
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: "bold", padding: "1px 0" }}>DN No:</td>
                <td style={{ textAlign: "right" }}>{data?.NewBillNo || data?.BillNo || "DRAFT"}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold", padding: "1px 0" }}>Date:</td>
                <td style={{ textAlign: "right" }}>
                  {data?.BillDate ? dayjs(data.BillDate).format("DD-MM-YYYY") : dayjs().format("DD-MM-YYYY")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <Divider sx={{ my: 0.5, borderColor: "black" }} />

        <div>
          <Typography sx={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: "bold", color: "grey.700" }}>
            Deliver To:
          </Typography>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
            {data?.BillPartyName || "Cash"}
          </Typography>
          {data?.Ph && (
            <Typography sx={{ fontSize: "0.8rem" }}>
              Phone: {data.Ph}
            </Typography>
          )}
          {data?.vat_no && (
            <Typography sx={{ fontSize: "0.8rem" }}>
              VAT NO: {data.vat_no}
            </Typography>
          )}
        </div>
      </Stack>
    </Stack>
  );
};

const PrintTable = ({ sectionItems }) => {
  return (
    <div style={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
      <table
        style={{
          width: "100%",
          height: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed"
        }}
      >
        <thead>
          <tr style={{ height: "30px", backgroundColor: "#f5f5f5" }}>
            <th
              style={{
                textAlign: "center",
                borderBottom: "1px solid black",
                borderRight: "1px solid black",
                fontSize: "12px",
                fontWeight: "bold",
                width: "8%",
              }}
            >
              S.No
            </th>
            <th
              style={{
                textAlign: "left",
                borderBottom: "1px solid black",
                borderRight: "1px solid black",
                paddingLeft: "8px",
                fontSize: "12px",
                fontWeight: "bold",
                width: "62%",
              }}
            >
              Item Description
            </th>
            <th
              style={{
                textAlign: "center",
                borderBottom: "1px solid black",
                borderRight: "1px solid black",
                fontSize: "12px",
                fontWeight: "bold",
                width: "15%",
              }}
            >
              UOM
            </th>
            <th
              style={{
                textAlign: "center",
                borderBottom: "1px solid black",
                fontSize: "12px",
                fontWeight: "bold",
                width: "15%",
              }}
            >
              Qty Sent
            </th>
          </tr>
        </thead>
        <tbody>
          {sectionItems.map((prod, index) => (
            <tr
              key={prod.id || `item-${index}`}
              style={{ height: "35px" }}
            >
              <td
                style={{
                  textAlign: "center",
                  verticalAlign: "middle",
                  borderRight: "1px solid black",
                  borderBottom: "1px solid black",
                  fontSize: "12px",
                }}
              >
                {prod.id}
              </td>
              <td
                style={{
                  textAlign: "left",
                  verticalAlign: "middle",
                  borderRight: "1px solid black",
                  borderBottom: "1px solid black",
                  paddingLeft: "8px",
                  fontSize: "12px",
                }}
              >
                {prod.ItemNameTextField}
              </td>
              <td
                style={{
                  textAlign: "center",
                  verticalAlign: "middle",
                  borderRight: "1px solid black",
                  borderBottom: "1px solid black",
                  fontSize: "12px",
                }}
              >
                {prod.Typ || "PCS"}
              </td>
              <td
                style={{
                  textAlign: "center",
                  verticalAlign: "middle",
                  borderBottom: "1px solid black",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                {prod.qty}
              </td>
            </tr>
          ))}
          {/* Stretches the vertical lines down to the footer */}
          <tr style={{ height: "auto" }}>
            <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
            <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
            <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
            <td>&nbsp;</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const PrintFooter = ({ company }) => {
  return (
    <Stack
      direction="row"
      style={{
        borderTop: "1px solid black",
        backgroundColor: "white",
        height: "7rem",
        marginTop: "auto"
      }}
    >
      {/* Left: Goods received validation */}
      <Stack
        direction="column"
        sx={{
          width: "50%",
          borderRight: "1px solid black",
          p: 1.5,
          justifyContent: "space-between"
        }}
      >
        <Typography sx={{ fontSize: "0.8rem", fontStyle: "italic" }}>
          Received the above-mentioned items in good condition.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <Typography sx={{ fontSize: "0.85rem", fontWeight: "bold" }}>Received By (Name & Sig):</Typography>
          <Box sx={{ borderBottom: "1px dashed black", flexGrow: 1, height: "1.5rem" }} />
        </Stack>
      </Stack>

      {/* Right: Supplier validation */}
      <Stack
        direction="column"
        sx={{
          width: "50%",
          p: 1.5,
          justifyContent: "space-between",
          textAlign: "right"
        }}
      >
        <Typography sx={{ fontSize: "0.85rem", fontWeight: "bold" }}>
          For {company.Name}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mt: 3 }}>
          <Box sx={{ borderBottom: "1px dashed black", flexGrow: 1, height: "1.5rem" }} />
          <Typography sx={{ fontSize: "0.85rem", fontWeight: "bold" }}>Authorized Signatory</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

// --- Main DeliveryNotePrint Component ---

const DeliveryNotePrint = () => {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const { data = {}, tableData = [], navigateBack = -1 } = location.state || {};

  const company = useSelector((state) => state.common.companyInfo) || {};
  const companyLogo = company.logo ? JSON.parse(company.logo)?.img : null;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@page { size: A4; margin: 0; } body { margin: 0; }`,
    onAfterPrint: () => {
      navigate(navigateBack);
    },
  });

  useEffect(() => {
    if (company && Object.keys(company).length > 0) {
      const timeoutId = setTimeout(() => {
        handlePrint();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [company, handlePrint]);

  // --- PAGE LAYOUT CONSTANTS ---
  const A4_HEIGHT_PX = 1123;
  const A4_WIDTH_PX = 794;

  const generateSections = (tableData) => {
    const dataWithIds = tableData.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
    
    // For Delivery notes, we fit everything on one page if possible.
    // If there are many items, we divide them (approx 20 items per page).
    const itemsPerPage = 20;
    const sections = [];
    for (let i = 0; i < dataWithIds.length; i += itemsPerPage) {
      sections.push(dataWithIds.slice(i, i + itemsPerPage));
    }

    if (sections.length === 0) {
      sections.push([]);
    }
    return sections;
  };

  const sectionsData = generateSections(tableData);
  const totalPages = sectionsData.length;

  const renderSection = (sectionItems, sectionIndex) => {
    const isLastPage = sectionIndex === totalPages - 1;

    return (
      <div
        key={`page-${sectionIndex}`}
        style={{
          width: `${A4_WIDTH_PX}px`,
          height: `${A4_HEIGHT_PX}px`,
          boxSizing: "border-box",
          padding: `20px`,
          display: "flex",
          flexDirection: "column",
          pageBreakAfter: isLastPage ? "auto" : "always",
          color: "black",
          backgroundColor: "white",
        }}
      >
        <div
          style={{
            border: "1px solid black",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%"
          }}
        >
          <PrintHeader
            data={data}
            company={company}
            companyLogo={companyLogo}
          />

          <PrintTable
            sectionItems={sectionItems}
          />

          <PrintFooter
            company={company}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      <div ref={componentRef}>
        {sectionsData.map((sectionItems, index) =>
          renderSection(sectionItems, index)
        )}
      </div>
    </div>
  );
};

export default DeliveryNotePrint;
